import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  attachRolePermission,
  createRole,
  deleteRole,
  detachRolePermission,
  updateRole,
  type Role,
} from './api';

async function diffPermissions(
  roleId: string,
  desired: Set<string>,
  baseline: Set<string>,
): Promise<void> {
  const grant = [...desired].filter((key) => !baseline.has(key));
  const revoke = [...baseline].filter((key) => !desired.has(key));
  await Promise.all([
    ...grant.map((key) => attachRolePermission(roleId, key)),
    ...revoke.map((key) => detachRolePermission(roleId, key)),
  ]);
}

export function useRoleMutations() {
  const qc = useQueryClient();
  // The tenant role list is cached under more than one key: the RBAC editor
  // reads ['tenant-roles'], while the members and invitations screens read
  // ['roles']. Invalidate both so a newly created role immediately shows up
  // wherever it can be assigned, not just on the roles page.
  const invalidateRoles = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['tenant-roles'] }),
      qc.invalidateQueries({ queryKey: ['roles'] }),
    ]);
  };
  const invalidatePerms = (roleId: string): Promise<void> =>
    qc.invalidateQueries({ queryKey: ['tenant-role-permissions', roleId] });

  const create = useMutation({
    mutationFn: async (input: {
      name: string;
      slug: string;
      description: string | null;
      desired: Set<string>;
    }): Promise<Role> => {
      const role = await createRole({
        name: input.name,
        slug: input.slug,
        description: input.description,
      });
      if (input.desired.size > 0) {
        await diffPermissions(role.id, input.desired, new Set());
        await invalidatePerms(role.id);
      }
      await invalidateRoles();
      return role;
    },
  });

  const update = useMutation({
    mutationFn: async (input: {
      role: Role;
      name: string;
      slug: string;
      description: string | null;
      desired: Set<string>;
      baseline: Set<string>;
    }): Promise<Role> => {
      const { role } = input;
      let next = role;
      const changedMeta =
        input.name !== role.name ||
        input.slug !== role.slug ||
        (input.description ?? '') !== (role.description ?? '');
      if (changedMeta) {
        next = await updateRole(role.id, {
          name: input.name,
          slug: input.slug,
          description: input.description,
        });
      }
      const permsChanged =
        input.desired.size !== input.baseline.size ||
        [...input.desired].some((key) => !input.baseline.has(key));
      if (permsChanged) {
        await diffPermissions(role.id, input.desired, input.baseline);
        await invalidatePerms(role.id);
      }
      await invalidateRoles();
      return next;
    },
  });

  const remove = useMutation({
    mutationFn: async (roleId: string): Promise<void> => {
      await deleteRole(roleId);
      await invalidateRoles();
    },
  });

  return { create, update, remove };
}
